/**
 * Structured frontend logger.
 * Outputs JSON to console in dev, silences debug in prod.
 * Ready to pipe to a backend /api/v1/log endpoint later.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  userId?: string
  page?: string
  flow?: string
  endpoint?: string
  [key: string]: unknown
}

const IS_PROD = typeof import.meta !== 'undefined' && import.meta.env?.PROD

function write(level: LogLevel, message: string, context?: LogContext): void {
  if (IS_PROD && level === 'debug') return

  const entry = {
    level,
    message,
    ...context,
    ts: new Date().toISOString(),
  }

  switch (level) {
    case 'error':
      console.error(`[${level}]`, message, context ?? '')
      break
    case 'warn':
      console.warn(`[${level}]`, message, context ?? '')
      break
    case 'debug':
      console.debug(`[${level}]`, message, context ?? '')
      break
    default:
      console.log(`[${level}]`, message, context ?? '')
  }

  // Future: ship `entry` to backend
  void entry
}

export const logger = {
  debug: (msg: string, ctx?: LogContext) => write('debug', msg, ctx),
  info: (msg: string, ctx?: LogContext) => write('info', msg, ctx),
  warn: (msg: string, ctx?: LogContext) => write('warn', msg, ctx),
  error: (msg: string, ctx?: LogContext) => write('error', msg, ctx),

  logApiError: (endpoint: string, status: number, message?: string, ctx?: LogContext) =>
    write('error', `API ${status} ${endpoint}`, { endpoint, status, message, ...ctx }),
}
